import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { progressLabel, summarizeResults, type FileResult, type QueueProgress } from "@/lib/uploadQueue"

interface UploadBatchStatusProps {
  /** Live progress while the queue runs, null otherwise. */
  progress: QueueProgress | null
  /** Final results (including files skipped before the queue started), null while nothing finished. */
  results: FileResult[] | null
  onCancel?: () => void
}

/** Shared by the token-link page and the in-app modal: "Foto x von n", pause note, cancel, final summary. */
export function UploadBatchStatus({ progress, results, onCancel }: UploadBatchStatusProps) {
  if (progress) {
    const done = progress.results.length
    const percent = progress.total > 0 ? Math.round((done / progress.total) * 100) : 0
    return (
      <div className="space-y-3" data-testid="upload-progress" aria-live="polite">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span data-testid="upload-progress-label">{progressLabel(progress)}</span>
          </span>
          {onCancel && (
            <Button type="button" variant="outline" size="sm" onClick={onCancel} data-testid="button-cancel-upload">
              Abbrechen
            </Button>
          )}
        </div>
        <div className="w-full bg-secondary rounded-full h-2">
          <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
        </div>
        {progress.phase === "waiting" && (
          <p className="text-xs text-muted-foreground" data-testid="upload-waiting">
            Kurze Pause wegen des Upload-Limits, es geht in etwa {progress.waitSeconds} Sekunden automatisch weiter.
          </p>
        )}
      </div>
    )
  }

  if (results && results.length > 0) {
    const problems = results.filter((r) => r.status !== "ok")
    return (
      <div className="space-y-2 text-sm" data-testid="upload-summary">
        <p className="font-medium">{summarizeResults(results)}</p>
        {problems.length > 0 && (
          <ul className="space-y-1 text-muted-foreground">
            {problems.map((r, i) => (
              <li key={`${r.file.name}-${i}`}>
                {r.file.name}: {r.status === "failed" ? "fehlgeschlagen" : "übersprungen"} ({r.reason})
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }
  return null
}
